/**
 * Tagebuch und Zusammenhänge: „Warum war das Training schwach?"
 *
 * Die App **beantwortet** die Frage nicht. Sie legt daneben, was in Frage kommt —
 * Essen der letzten 24 Stunden, Zeit seit der letzten Mahlzeit, Schlaf der Nacht
 * davor — und überlässt den Schluss dem Menschen. Eine Diagnose wäre eine
 * Behauptung, die die App nicht belegen kann, und der Schritt zum Medizinprodukt.
 *
 * **Schwach ist, was der Nutzer so bewertet.** Aus Gewichten oder Wiederholungen
 * abzuleiten, ob eine Einheit schlecht war, hieße zu raten: Ein Deload sieht in
 * den Zahlen aus wie ein Einbruch.
 *
 * **Fehlend ist `null`, nicht 0.** Wer gestern nichts protokolliert hat, hat
 * nicht 0 kcal gegessen. Die Oberfläche schreibt „nichts protokolliert" hin.
 */

import { isoDateInZone } from './time.js';

/** Skala für Bewertung, Energie, Stimmung, Muskelkater: 1 (schlecht) bis 5 (gut). */
export const SCALE_MIN = 1;
export const SCALE_MAX = 5;

/** Bis hierher gilt eine Einheit als schwach — nach eigener Bewertung. */
export const WEAK_RATING_MAX = 2;

export function isOnScale(value: number): boolean {
  return Number.isInteger(value) && value >= SCALE_MIN && value <= SCALE_MAX;
}

export function isWeakSession(rating: number | null | undefined): boolean {
  return typeof rating === 'number' && rating <= WEAK_RATING_MAX;
}

/** Wie weit vor dem Training das Essen zählt. */
export const CONTEXT_WINDOW_HOURS = 24;

export interface ContextMeal {
  eatenAt: string;
  kcal: number;
  protein: number;
}

export interface ContextJournal {
  date: string;
  updatedAt: string;
  sleepHours: number | null;
  energy: number | null;
}

export interface SessionContext {
  /** Summe der letzten 24 h; `null`, wenn nichts protokolliert ist. */
  kcal: number | null;
  protein: number | null;
  meals: number;
  /** Minuten seit der letzten Mahlzeit vor dem Start; `null` ohne Mahlzeit im Fenster. */
  minutesSinceMeal: number | null;
  /** Aus dem Tagebuch des Trainingstages — die Nacht davor. */
  sleepHours: number | null;
  energy: number | null;
}

/**
 * Was vor einer Einheit war.
 *
 * Mahlzeiten zählen, wenn sie in den 24 Stunden **vor dem Start** gegessen
 * wurden; was währenddessen oder danach kam, hat das Training nicht beeinflusst.
 * Der Schlaf kommt aus dem Tagebuch des Trainingstages in Ortszeit — wer
 * morgens einträgt, wie er geschlafen hat, meint die Nacht davor.
 */
export function contextBeforeSession(input: {
  startedAt: string;
  timeZone: string;
  meals: readonly ContextMeal[];
  journal: readonly ContextJournal[];
}): SessionContext {
  const start = Date.parse(input.startedAt);
  const from = start - CONTEXT_WINDOW_HOURS * 60 * 60 * 1000;

  const inWindow = input.meals.filter((meal) => {
    const at = Date.parse(meal.eatenAt);
    return at > from && at <= start;
  });

  const last = inWindow.reduce<number | null>((latest, meal) => {
    const at = Date.parse(meal.eatenAt);
    return latest === null || at > latest ? at : latest;
  }, null);

  const day = isoDateInZone(new Date(start), input.timeZone);
  const entry = input.journal
    .filter((candidate) => candidate.date === day)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];

  return {
    kcal: inWindow.length > 0 ? Math.round(inWindow.reduce((sum, m) => sum + m.kcal, 0)) : null,
    protein:
      inWindow.length > 0 ? Math.round(inWindow.reduce((sum, m) => sum + m.protein, 0)) : null,
    meals: inWindow.length,
    minutesSinceMeal: last === null ? null : Math.round((start - last) / 60_000),
    sleepHours: entry?.sleepHours ?? null,
    energy: entry?.energy ?? null,
  };
}

// --- Gedanken (E14) ----------------------------------------------------------

export const NOTE_BODY_MAX = 20_000;
export const NOTE_KINDS = ['event', 'household', 'feed', 'training'] as const;
export type NoteEventKind = (typeof NOTE_KINDS)[number];

/**
 * Schlagwörter aus dem Text: `#urlaub`, `#Familie` → `urlaub`, `familie`.
 * Buchstaben (auch Umlaute), Ziffern, `_` und `-`; doppelte nur einmal.
 */
export function extractTags(text: string): string[] {
  const found = new Set<string>();
  for (const match of text.matchAll(/(?:^|[^\p{L}\p{N}_&/])#([\p{L}\p{N}_-]{2,40})/gu)) {
    found.add(match[1]!.toLowerCase());
  }
  return [...found];
}

/**
 * „An diesem Tag": dieselbe Kalenderstelle vor einem Monat und in früheren
 * Jahren. Am 31. gibt es im Vormonat oft keinen — dann eben nicht, statt eines
 * erfundenen Nachbartags.
 */
export function onThisDayDates(
  today: string,
  years = 10,
): { date: string; kind: 'month' | 'year'; ago: number }[] {
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const result: { date: string; kind: 'month' | 'year'; ago: number }[] = [];
  const pad = (value: number) => String(value).padStart(2, '0');
  const valid = (year: number, month: number, day: number) =>
    new Date(Date.UTC(year, month - 1, day)).getUTCDate() === day;
  const prevMonth = m === 1 ? 12 : m - 1;
  const prevMonthYear = m === 1 ? y - 1 : y;
  if (valid(prevMonthYear, prevMonth, d)) {
    result.push({ date: `${prevMonthYear}-${pad(prevMonth)}-${pad(d)}`, kind: 'month', ago: 1 });
  }
  for (let ago = 1; ago <= years; ago += 1) {
    if (valid(y - ago, m, d)) {
      result.push({ date: `${y - ago}-${pad(m)}-${pad(d)}`, kind: 'year', ago });
    }
  }
  return result;
}

/** Suche über Titel, Text und Schlagwörter — ohne Groß/klein, Wörter alle enthalten. */
export function noteMatches(
  note: { title: string | null; body: string; tags: readonly string[] },
  query: string,
): boolean {
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.replace(/^#/, ''))
    .filter(Boolean);
  if (words.length === 0) return true;
  const haystack = `${note.title ?? ''}\n${note.body}\n${note.tags.join(' ')}`.toLowerCase();
  return words.every((word) => haystack.includes(word));
}
